import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_UI_LANGUAGE, normalizeUiLanguage, type UiLanguage } from '../i18n/language';

export const useLanguage = () => {
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n: i18nInstance } = useTranslation();

  const supportedLanguages = ['en', 'fr'];
  const defaultLanguage = DEFAULT_UI_LANGUAGE;

  const currentLanguage: UiLanguage =
    lang && supportedLanguages.includes(lang) ? normalizeUiLanguage(lang) : defaultLanguage;

  useEffect(() => {
    const uiLang = normalizeUiLanguage(lang);
    if (lang && supportedLanguages.includes(lang) && i18nInstance.language !== uiLang) {
      i18nInstance.changeLanguage(uiLang);
      localStorage.setItem('i18nextLng', uiLang);
    }
  }, [lang, i18nInstance]);

  const getPathWithoutLang = (path: string = location.pathname): string => {
    const pathWithoutLang = path.replace(/^\/[a-z]{2}(\/|$)/, '/');
    return pathWithoutLang === '' ? '/' : pathWithoutLang;
  };

  const getPathWithLang = (path: string, targetLang?: string): string => {
    const langToUse = normalizeUiLanguage(targetLang || currentLanguage);
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const pathWithoutLang = getPathWithoutLang(cleanPath);
    if (pathWithoutLang === '/') return `/${langToUse}`;
    return `/${langToUse}${pathWithoutLang}`;
  };

  const navigateWithLang = (path: string, options?: { replace?: boolean; state?: any }) => {
    navigate(getPathWithLang(path), options);
  };

  const switchLanguage = (newLang: string) => {
    const uiLang = normalizeUiLanguage(newLang);
    if (uiLang !== DEFAULT_UI_LANGUAGE) return;
    const currentPath = getPathWithoutLang();
    const newPath = getPathWithLang(currentPath, uiLang);
    localStorage.setItem('i18nextLng', uiLang);
    navigate(newPath, { replace: true });
  };

  return {
    currentLanguage,
    supportedLanguages,
    defaultLanguage,
    getPathWithoutLang,
    getPathWithLang,
    navigateWithLang,
    switchLanguage,
  };
};
